import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  DEFAULT_MEASUREMENT_SETTINGS,
  formatLength as formatLengthLib,
  parseLength as parseLengthLib,
  formatArea as formatAreaLib,
  parseArea as parseAreaLib,
  lengthUnitLabel as lengthUnitLabelLib,
  areaUnitLabel as areaUnitLabelLib,
  type LengthFormat,
  type AreaFormat,
  type MeasurementSettings,
  type ParseOptions,
} from '@mn/shared';
import { apiClient } from './api';

export interface MeasurementContextValue {
  lengthFormat: LengthFormat;
  areaFormat: AreaFormat;
  isLoading: boolean;
  /** Format canonical meters for display in the active length format. */
  formatLength: (meters: number | null | undefined) => string;
  /** Parse a length string (active format) to canonical meters. */
  parseLength: (text: string, options?: ParseOptions) => number | null;
  /** Format canonical m² for display in the active area format. */
  formatArea: (m2: number | null | undefined) => string;
  /** Parse an area string (active format) to canonical m². */
  parseArea: (text: string) => number | null;
  /** Short unit label for the active length format (e.g. `ft-in`, `m`). */
  lengthUnitLabel: () => string;
  /** Short unit label for the active area format (e.g. `sq ft`, `m²`). */
  areaUnitLabel: () => string;
  refresh: () => void;
}

const MeasurementContext = createContext<MeasurementContextValue | null>(null);

export function MeasurementProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<MeasurementSettings>(DEFAULT_MEASUREMENT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(() => {
    // Context provider runs before auth; manual fetch is the sanctioned exception.
    apiClient
      .get<{ data: Partial<MeasurementSettings> }>('/public/measurement-settings')
      .then((res) => {
        setSettings({ ...DEFAULT_MEASUREMENT_SETTINGS, ...res.data });
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const value = useMemo<MeasurementContextValue>(() => {
    const { lengthFormat, areaFormat } = settings;
    return {
      lengthFormat,
      areaFormat,
      isLoading,
      formatLength: (meters) => formatLengthLib(meters, lengthFormat),
      parseLength: (text, options) => parseLengthLib(text, lengthFormat, options),
      formatArea: (m2) => formatAreaLib(m2, areaFormat),
      parseArea: (text) => parseAreaLib(text, areaFormat),
      lengthUnitLabel: () => lengthUnitLabelLib(lengthFormat),
      areaUnitLabel: () => areaUnitLabelLib(areaFormat),
      refresh: fetchSettings,
    };
  }, [settings, isLoading, fetchSettings]);

  return <MeasurementContext.Provider value={value}>{children}</MeasurementContext.Provider>;
}

export function useMeasurement(): MeasurementContextValue {
  const ctx = useContext(MeasurementContext);
  if (!ctx) {
    throw new Error('useMeasurement must be used within a MeasurementProvider');
  }
  return ctx;
}
